import { useEffect, useMemo, useState } from "react";
import { useEditorStore } from "../app/store";
import { Icon } from "../components/Icon";
import type { ControlDefinition } from "../domain/instrument";
import { instrumentBundleForDocument } from "../instruments/catalog";
import { cableEndpoints } from "../domain/patch";
import { getNormalledRouteStates, validateSignalPatch } from "../domain/signal-graph";
import { WireframePanel } from "./WireframePanel";
import { cachedCompiledInstrumentRuntime } from "../workspace/compiled-instrument";
import { externalEndpoints, utilityNodes, workspaceDefinition } from "../workspace/workspace-ports";
import "../styles/print.css";

type PrintProfile = "diagram" | "concise" | "recall";

export function PrintPreview({ onClose }: { onClose: () => void }) {
  const [profile, setProfile] = useState<PrintProfile>("concise");
  const { activeDocument, cables, controlValues, updatePrintSettings } = useEditorStore();
  const settings = activeDocument.printSettings;
  const instrumentBundle = useMemo(
    () => instrumentBundleForDocument(activeDocument),
    [activeDocument],
  );
  const instrumentRuntime = useMemo(
    () => cachedCompiledInstrumentRuntime(instrumentBundle.document),
    [instrumentBundle],
  );
  const instrument = instrumentRuntime.definition;
  const definition = useMemo(
    () => workspaceDefinition(activeDocument, instrument),
    [activeDocument, instrument],
  );
  const endpoints = useMemo(() => externalEndpoints(activeDocument), [activeDocument]);
  const utilities = useMemo(() => utilityNodes(activeDocument), [activeDocument]);
  const portNames = useMemo(
    () => new Map(definition.ports.map((port) => [port.id, port.label])),
    [definition],
  );
  const changedControls = instrument.controls.filter(
    (control) =>
      controlValues[control.id] !== undefined &&
      Math.abs(controlValues[control.id]! - control.defaultValue) > 0.001,
  );
  const normalStates = useMemo(
    () => getNormalledRouteStates(definition, cables, controlValues),
    [cables, controlValues, definition],
  );
  const brokenNormals = normalStates.filter((state) => state.status === "broken");
  const signalWarnings = useMemo(
    () => validateSignalPatch(definition, cables),
    [cables, definition],
  );
  const describeEndpoint = (endpoint: (typeof cables)[number]["endpointA"]) =>
    endpoint.kind === "loose"
      ? (endpoint.label ?? "Loose end")
      : `${portNames.get(endpoint.portId) ?? endpoint.portId}${
          endpoint.plugStyle === "stackable" ? " [STACK]" : ""
        }`;
  const describeAnnotationTarget = (
    target: (typeof activeDocument.workspace.annotations)[number]["target"],
  ) => {
    if (target.kind === "patch") return "Whole patch";
    if (target.kind === "section") {
      return (
        instrument.sections.find((section) => section.id === target.sectionId)?.label ??
        "Unavailable section"
      );
    }
    if (target.kind === "control") {
      return (
        instrument.controls.find((control) => control.id === target.controlId)?.label ??
        "Unavailable control"
      );
    }
    if (target.kind === "port") return portNames.get(target.portId) ?? "Unavailable port";
    const cable = cables.find((item) => item.id === target.cableId);
    return cable
      ? `Cable ${cable.number}${cable.label ? ` — ${cable.label}` : ""}`
      : "Unavailable cable";
  };
  const describeValue = (control: ControlDefinition) => {
    const value = controlValues[control.id] ?? control.defaultValue;
    if (control.options) {
      return control.options[Math.round(value * (control.options.length - 1))]?.label ?? "—";
    }
    return `${Math.round(value * 100)}%`;
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className={`print-preview print-${settings.paper} print-${settings.orientation}`}>
      <style>{`@page { size: ${settings.paper === "a4" ? "A4" : "Letter"} ${settings.orientation}; margin: 9mm; }`}</style>
      <header className="print-preview-toolbar">
        <div className="print-preview-title">
          <strong>Print preview</strong>
          <span>Dedicated monochrome wireframe output</span>
        </div>
        <label>
          <span>Profile</span>
          <select
            onChange={(event) => setProfile(event.target.value as PrintProfile)}
            value={profile}
          >
            <option value="diagram">Diagram only</option>
            <option value="concise">Diagram + concise list</option>
            <option value="recall">Recall checklist</option>
          </select>
        </label>
        <label>
          <span>Paper</span>
          <select
            onChange={(event) =>
              updatePrintSettings({ ...settings, paper: event.target.value as "a4" | "letter" })
            }
            value={settings.paper}
          >
            <option value="a4">A4</option>
            <option value="letter">Letter</option>
          </select>
        </label>
        <label>
          <span>Orientation</span>
          <select
            onChange={(event) =>
              updatePrintSettings({
                ...settings,
                orientation: event.target.value as "landscape" | "portrait",
              })
            }
            value={settings.orientation}
          >
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </select>
        </label>
        <label>
          <span>Output</span>
          <select
            onChange={(event) =>
              updatePrintSettings({
                ...settings,
                palette: event.target.value as "grayscale" | "monochrome",
              })
            }
            value={settings.palette}
          >
            <option value="monochrome">High-contrast monochrome</option>
            <option value="grayscale">Grayscale</option>
          </select>
        </label>
        <div className="print-preview-actions">
          <button onClick={onClose} type="button">
            <Icon name="close" size={16} /> Close
          </button>
          <button className="is-primary" onClick={() => window.print()} type="button">
            <Icon name="print" size={16} /> Print / Save PDF
          </button>
        </div>
      </header>

      <main className="print-preview-stage">
        <article className="print-sheet">
          <header className="print-sheet-header">
            <div>
              <span className="print-kicker">Patchwork · Patch document</span>
              <h1>{activeDocument.title}</h1>
              {activeDocument.metadata.description && <p>{activeDocument.metadata.description}</p>}
            </div>
            <dl>
              <div>
                <dt>Instrument</dt>
                <dd>{instrument.displayName}</dd>
              </div>
              <div>
                <dt>Revision</dt>
                <dd>{activeDocument.revision.number}</dd>
              </div>
              <div>
                <dt>Tempo</dt>
                <dd>
                  {activeDocument.metadata.tempo ? `${activeDocument.metadata.tempo} BPM` : "—"}
                </dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{new Date(activeDocument.updatedAt).toLocaleDateString()}</dd>
              </div>
            </dl>
          </header>

          <section className="print-diagram">
            <WireframePanel
              cables={cables}
              controlValues={controlValues}
              palette={settings.palette}
              runtime={instrumentRuntime}
            />
            {(endpoints.length > 0 || utilities.length > 0) && (
              <div className="print-workspace-nodes">
                {endpoints.map((endpoint) => (
                  <div className="print-node-card" key={endpoint.id}>
                    <strong>{endpoint.deviceName ?? endpoint.displayName}</strong>
                    <span>{endpoint.portName ?? endpoint.displayName}</span>
                    <i className="print-node-socket" />
                    <small>
                      {endpoint.direction} · {endpoint.signalType}
                    </small>
                  </div>
                ))}
                {utilities.map((utility) => (
                  <div className="print-node-card print-utility-card" key={utility.id}>
                    <strong>{utility.displayName}</strong>
                    <span>
                      {utility.utilityType.includes("attenuator")
                        ? `Attenuator · ${Math.round(
                            (utility.controls.find((control) => control.id === "level")?.value ??
                              1) * 100,
                          )}%`
                        : utility.utilityType === "splitter-cable"
                          ? `Splitter cable · ${utility.ports.length - 1} branches`
                          : utility.utilityType === "stackable-junction"
                            ? `Cable junction · ${utility.ports.length} ends`
                            : `Passive mult · ${utility.ports.length} sockets`}
                    </span>
                    <div>
                      {utility.ports.map((port) => (
                        <i className="print-node-socket" key={port.id} title={port.label} />
                      ))}
                    </div>
                    <small>
                      {utility.manufacturer ?? "Unbranded"} {utility.model ?? ""}
                    </small>
                  </div>
                ))}
              </div>
            )}
          </section>

          {profile !== "diagram" && (
            <section className={`print-details print-profile-${profile}`}>
              <div className="print-detail-block">
                <h2>{profile === "recall" ? "Control checklist" : "Changed controls"}</h2>
                {changedControls.length ? (
                  <ul>
                    {changedControls.map((control) => (
                      <li key={control.id}>
                        {profile === "recall" && <i className="print-checkbox" />}
                        <span>{control.label}</span>
                        <b>{describeValue(control)}</b>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No controls differ from the documented defaults.</p>
                )}
              </div>
              <div className="print-detail-block print-connections">
                <h2>{profile === "recall" ? "Cable checklist" : "Cable index"}</h2>
                {cables.length ? (
                  <ol>
                    {cables.map((cable) => (
                      <li key={cable.id}>
                        {profile === "recall" && <i className="print-checkbox" />}
                        <b>{cable.number}</b>
                        <span>
                          {cable.prepared && <strong>[PREPARED] </strong>}
                          {cable.label && <strong>{cable.label}: </strong>}
                          {describeEndpoint(cable.endpointA)}
                        </span>
                        <em>→</em>
                        <span>
                          {describeEndpoint(cable.endpointB)}
                          {cable.branches?.map((branch, index) => (
                            <small key={index}>
                              {" "}
                              · branch {index + 2}: {describeEndpoint(branch)}
                            </small>
                          ))}
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p>No patch cables documented.</p>
                )}
              </div>
              <div className="print-detail-block print-notes">
                <h2>Patch information</h2>
                <dl>
                  <div>
                    <dt>Author</dt>
                    <dd>{activeDocument.author || "—"}</dd>
                  </div>
                  <div>
                    <dt>Category</dt>
                    <dd>{activeDocument.metadata.category || "—"}</dd>
                  </div>
                  <div>
                    <dt>Tags</dt>
                    <dd>{activeDocument.metadata.tags.join(", ") || "—"}</dd>
                  </div>
                  <div>
                    <dt>Cables</dt>
                    <dd>{cables.length}</dd>
                  </div>
                  <div>
                    <dt>Internal normals</dt>
                    <dd>
                      {normalStates.length - brokenNormals.length} active · {brokenNormals.length}{" "}
                      broken
                    </dd>
                  </div>
                  <div>
                    <dt>Routing checks</dt>
                    <dd>
                      {signalWarnings.filter((warning) => warning.severity === "warning").length}{" "}
                      warnings
                    </dd>
                  </div>
                  <div>
                    <dt>Media</dt>
                    <dd>
                      {activeDocument.attachments.filter((item) => item.kind === "photo").length}{" "}
                      photos ·{" "}
                      {activeDocument.attachments.filter((item) => item.kind === "audio").length}{" "}
                      audio
                    </dd>
                  </div>
                  <div>
                    <dt>Settings</dt>
                    <dd>{activeDocument.customSettings.length}</dd>
                  </div>
                  <div>
                    <dt>Linked notes</dt>
                    <dd>{activeDocument.workspace.annotations.length}</dd>
                  </div>
                  <div>
                    <dt>States</dt>
                    <dd>{activeDocument.performanceStates.length}</dd>
                  </div>
                </dl>
                {activeDocument.notes.body && (
                  <div className="print-patch-note">
                    <strong>Notes</strong>
                    <p>{activeDocument.notes.body}</p>
                  </div>
                )}
                {activeDocument.workspace.annotations.length > 0 && (
                  <div className="print-structured-docs">
                    <strong>Linked annotations</strong>
                    <ul>
                      {activeDocument.workspace.annotations.map((annotation) => (
                        <li key={annotation.id}>
                          <span>{describeAnnotationTarget(annotation.target)}</span>
                          <em>{annotation.text}</em>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {endpoints.length > 0 && (
                  <div className="print-structured-docs">
                    <strong>External equipment</strong>
                    <ul>
                      {endpoints.map((endpoint) => (
                        <li key={endpoint.id}>
                          <span>
                            {endpoint.deviceName ?? endpoint.displayName} —{" "}
                            {endpoint.portName ?? endpoint.displayName}
                          </span>
                          <b>
                            {endpoint.direction} · {endpoint.signalType} ·{" "}
                            {endpoint.connectorType ?? "other"}
                          </b>
                          {endpoint.voltageNotes && <em>{endpoint.voltageNotes}</em>}
                          {endpoint.description && <em>{endpoint.description}</em>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {utilities.length > 0 && (
                  <div className="print-structured-docs">
                    <strong>Patch utilities</strong>
                    <ul>
                      {utilities.map((utility) => (
                        <li key={utility.id}>
                          <span>{utility.displayName}</span>
                          <b>
                            {utility.utilityType.includes("attenuator")
                              ? `IN → OUT · ${Math.round(
                                  (utility.controls.find((control) => control.id === "level")
                                    ?.value ?? 1) * 100,
                                )}%`
                              : utility.utilityType === "splitter-cable" ||
                                  utility.utilityType === "stackable-junction"
                                ? (() => {
                                    const connected = utility.ports.filter((port) =>
                                      cables.some((cable) =>
                                        cableEndpoints(cable).some(
                                          (endpoint) =>
                                            endpoint.kind === "port" &&
                                            endpoint.portId === `utility:${utility.id}:${port.id}`,
                                        ),
                                      ),
                                    ).length;
                                    return `${connected}/${utility.ports.length} ends connected · ${
                                      utility.ports.length - connected
                                    } unused`;
                                  })()
                                : `${utility.ports.length} sockets · ${
                                    new Set(utility.ports.map((port) => port.groupId)).size
                                  } groups`}
                          </b>
                          {utility.userNotes && <em>{utility.userNotes}</em>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {activeDocument.customSettings.length > 0 && (
                  <div className="print-structured-docs">
                    <strong>Custom settings</strong>
                    <ul>
                      {activeDocument.customSettings.map((setting) => (
                        <li key={setting.id}>
                          <span>{setting.label}</span>
                          <b>{setting.value}</b>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {activeDocument.performanceStates.length > 0 && (
                  <div className="print-structured-docs">
                    <strong>Performance states</strong>
                    <ol>
                      {activeDocument.performanceStates.map((performanceState) => (
                        <li key={performanceState.id}>
                          <span>{performanceState.name}</span>
                          <b>
                            {Object.keys(performanceState.controlValues).length} controls ·{" "}
                            {performanceState.cableAssemblies.length} cables
                          </b>
                          {performanceState.notes && <em>{performanceState.notes}</em>}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                <div className="print-normal-routes">
                  <strong>Normal routing</strong>
                  {brokenNormals.length ? (
                    <ul>
                      {brokenNormals.map((state) => (
                        <li key={state.route.id}>
                          {profile === "recall" && <i className="print-checkbox" />}
                          <span>{state.route.label}</span>
                          <b>Broken</b>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>All {normalStates.length} factory normal routes active.</p>
                  )}
                </div>
                {profile === "recall" && (
                  <div className="print-signoff">
                    Recalled by: ____________________ &nbsp; Date: __________
                  </div>
                )}
              </div>
            </section>
          )}

          <footer className="print-sheet-footer">
            <span>Patch ID {activeDocument.id}</span>
            <span>Numbers and line patterns identify cables without colour.</span>
          </footer>
        </article>
      </main>
    </div>
  );
}
